const STORAGE_KEYS = {
  LAST_ACTIVITY_AT: 'idleLogoutLastActivityAt',
  LOGOUT_SIGNAL: 'idleLogoutSignal',
};

const SYNC_THROTTLE_MS = 1000;

export const resolveIdleLogoutMinutes = value => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
};

export const resolveIdleLogoutWarningMs = timeoutMs => {
  if (timeoutMs <= 30 * 1000) {
    return 5 * 1000;
  }

  if (timeoutMs <= 2 * 60 * 1000) {
    return 15 * 1000;
  }

  return 60 * 1000;
};

export const createIdleSessionManager = ({
  timeoutMs,
  warningMs = resolveIdleLogoutWarningMs(timeoutMs),
  onWarningChange = () => {},
  onTimeout = () => {},
  win = window,
  doc = document,
  now = () => Date.now(),
} = {}) => {
  let lastActivityAt = 0;
  let lastSyncedActivityAt = 0;
  let warningTimer = null;
  let timeoutTimer = null;
  let countdownTimer = null;
  let isRunning = false;
  let isTimingOut = false;

  const clearTimer = timerId => {
    if (timerId) {
      win.clearTimeout(timerId);
    }
    return null;
  };

  const clearIntervalTimer = timerId => {
    if (timerId) {
      win.clearInterval(timerId);
    }
    return null;
  };

  const emitWarningState = visible => {
    const remainingMs = Math.max(lastActivityAt + timeoutMs - now(), 0);
    onWarningChange({ visible, remainingMs });
  };

  const stopCountdown = () => {
    countdownTimer = clearIntervalTimer(countdownTimer);
  };

  const startCountdown = () => {
    if (countdownTimer) {
      return;
    }

    countdownTimer = win.setInterval(() => {
      const remainingMs = lastActivityAt + timeoutMs - now();

      if (remainingMs <= 0) {
        stopCountdown();
        return;
      }

      emitWarningState(true);
    }, 1000);
  };

  const writeStorageValue = (key, value) => {
    try {
      win.localStorage.setItem(key, value);
    } catch (error) {
      // Ignore localStorage failures in private mode or restricted contexts.
    }
  };

  const readActivityTimestamp = () => {
    try {
      return Number(win.localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY_AT)) || 0;
    } catch (error) {
      return 0;
    }
  };

  const broadcastLogout = () => {
    writeStorageValue(
      STORAGE_KEYS.LOGOUT_SIGNAL,
      JSON.stringify({ triggeredAt: now() })
    );
  };

  const handleTimeout = ({ broadcast = true } = {}) => {
    if (isTimingOut) {
      return;
    }

    isTimingOut = true;
    stopCountdown();
    emitWarningState(false);

    if (broadcast) {
      broadcastLogout();
    }

    onTimeout();
  };

  const scheduleTimers = () => {
    warningTimer = clearTimer(warningTimer);
    timeoutTimer = clearTimer(timeoutTimer);
    stopCountdown();

    const expiresAt = lastActivityAt + timeoutMs;
    const remainingMs = expiresAt - now();

    if (remainingMs <= 0) {
      handleTimeout();
      return;
    }

    if (remainingMs <= warningMs) {
      emitWarningState(true);
      startCountdown();
    } else {
      emitWarningState(false);
      warningTimer = win.setTimeout(() => {
        emitWarningState(true);
        startCountdown();
      }, remainingMs - warningMs);
    }

    timeoutTimer = win.setTimeout(() => handleTimeout(), remainingMs);
  };

  const setActivity = ({ sync = false, forceSync = false } = {}) => {
    if (!isRunning || isTimingOut) {
      return;
    }

    const currentTime = now();

    if (!forceSync && currentTime - lastActivityAt < SYNC_THROTTLE_MS) {
      return;
    }

    lastActivityAt = currentTime;
    scheduleTimers();

    if (sync && (forceSync || currentTime - lastSyncedActivityAt >= SYNC_THROTTLE_MS)) {
      lastSyncedActivityAt = currentTime;
      writeStorageValue(STORAGE_KEYS.LAST_ACTIVITY_AT, String(currentTime));
    }
  };

  const handleStorageEvent = event => {
    if (event.key === STORAGE_KEYS.LAST_ACTIVITY_AT && event.newValue) {
      const nextActivityAt = Number(event.newValue) || 0;

      if (nextActivityAt > lastActivityAt) {
        lastActivityAt = nextActivityAt;
        scheduleTimers();
      }
    }

    if (event.key === STORAGE_KEYS.LOGOUT_SIGNAL && event.newValue) {
      handleTimeout({ broadcast: false });
    }
  };

  const handleVisibilityChange = () => {
    if (doc.visibilityState === 'visible') {
      setActivity({ sync: true, forceSync: true });
    }
  };

  const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'focus'];
  const handleActivity = () => setActivity({ sync: true });

  const bindEvents = () => {
    activityEvents.forEach(eventName => {
      win.addEventListener(eventName, handleActivity, { passive: true });
    });
    doc.addEventListener('visibilitychange', handleVisibilityChange);
    win.addEventListener('storage', handleStorageEvent);
  };

  const unbindEvents = () => {
    activityEvents.forEach(eventName => {
      win.removeEventListener(eventName, handleActivity);
    });
    doc.removeEventListener('visibilitychange', handleVisibilityChange);
    win.removeEventListener('storage', handleStorageEvent);
  };

  return {
    start() {
      if (!timeoutMs || timeoutMs <= 0 || isRunning) {
        return;
      }

      isRunning = true;
      isTimingOut = false;
      bindEvents();

      const currentTime = now();
      lastActivityAt = Math.max(readActivityTimestamp(), currentTime);
      lastSyncedActivityAt = lastActivityAt;
      writeStorageValue(STORAGE_KEYS.LAST_ACTIVITY_AT, String(lastActivityAt));
      scheduleTimers();
    },
    stop() {
      if (!isRunning) {
        return;
      }

      isRunning = false;
      isTimingOut = false;
      warningTimer = clearTimer(warningTimer);
      timeoutTimer = clearTimer(timeoutTimer);
      stopCountdown();
      emitWarningState(false);
      unbindEvents();
    },
    markActive(options = {}) {
      setActivity({ sync: true, ...options });
    },
  };
};
